/**
 * Renders a group of conditions with and/or logic.
 * Matches ~/code/ui/app/components/condition-editor-v2/nodes/condition-group/view.gts
 */

import { useState } from 'react';
import { Box, Flex, Text, Stack, Badge, Icon, PlainButton } from '@customerio/pluma-components/react';
import type { GroupNode } from '../types';
import { ConditionNode } from './condition-node';
import * as styles from './condition-group.css';

interface ConditionGroupProps {
  node: GroupNode;
  depth?: number;
  workspaceId: number;
}

export function ConditionGroup({ node, depth = 0, workspaceId }: ConditionGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { children, operator } = node;

  if (children.length === 0) {
    return (
      <Text size="s" color="subtle">
        No conditions
      </Text>
    );
  }

  // Single child — render directly without group wrapper
  if (children.length === 1) {
    return <ConditionNode node={children[0]} depth={depth} workspaceId={workspaceId} />;
  }

  const groupLabel = operator === 'and' ? 'all' : 'any';

  return (
    <Box w="full" className={depth > 0 ? styles.nestedGroup : undefined}>
      {/* Descriptor: "Matches all/any of N conditions" */}
      <Flex gap="100" alignItems="center" mb={isExpanded ? '150' : '0'} justifyContent="space-between" w="full">
        <Flex gap="050" alignItems="center">
          {depth > 0 ? (
            <PlainButton
              cursor="pointer"
              display="inline-flex"
              alignItems="center"
              aria-label={isExpanded ? 'Collapse conditions' : 'Expand conditions'}
              onClick={() => setIsExpanded((v) => !v)}
            >
              <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="sm" />
            </PlainButton>
          ) : null}
          <Text size="xs" color="placeholder" display="inline-flex" gap="050" alignItems="center">
            Matches <Badge>{groupLabel}</Badge> of {children.length} conditions
          </Text>
        </Flex>
      </Flex>

      {isExpanded ? (
        <Stack gap="150">
          {children.map((child, index) => (
            <ConditionNode key={`${child.type}-${index}`} node={child} depth={depth + 1} workspaceId={workspaceId} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
